/**
 * Page 3 — Operating & Regulatory Context.
 *
 * Renders the framework-driven context block produced by
 * `buildTelecomContextBlock`. Placed immediately after the Assessment Profile
 * so reviewers and recipients see the framing before the executive narrative.
 *
 * Layout:
 *   Eyebrow:   OPERATING & REGULATORY CONTEXT (gold)
 *   Title:     Engagement Framing
 *   Summary paragraph
 *   Operating-footprint bullets
 *   Per-framework cards (Applicability / Requirements / Purpose)
 *   Additional context notes (optional)
 *   Legacy methodology notes (only when no frameworks selected)
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import type { TelecomContextBlock } from "@/reports/telecom/operatingContext";
import {
  buildVendorExposureTableRows,
  VENDOR_EXPOSURE_DISCLAIMER,
  type VendorExposureRow,
} from "@/reports/telecom/vendorExposure";

export const OPERATING_CONTEXT_RENDERER_VERSION =
  "operating-context-renderer:v2026-06-12-override-hanging-indent";

interface Ctx {
  clientOrgName?: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  pageNumber?: number;
  /** Phase 2.14 — Vendor Exposure rows (reviewer-managed). */
  vendorExposure?: VendorExposureRow[];
  /** Phase 2.14 — Reviewer confirmation for vendor exposure section. */
  vendorExposureConfirmed?: boolean;
}

function normalizeContextBody(block: TelecomContextBlock): { summary: string; bullets: string[] } {
  if (!block.overrideEnabled || block.bullets.length > 0) {
    return { summary: block.summary, bullets: block.bullets };
  }

  const summaryLines: string[] = [];
  const bullets: string[] = [];
  for (const line of String(block.summary ?? "").split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (summaryLines.length > 0 && summaryLines[summaryLines.length - 1] !== "") summaryLines.push("");
      continue;
    }
    const bulletMatch = trimmed.match(/^(?:[•▪◦‣\-*])\s+(.*)$/);
    if (bulletMatch?.[1]?.trim()) bullets.push(bulletMatch[1].trim());
    else summaryLines.push(trimmed);
  }

  if (bullets.length === 0) return { summary: block.summary, bullets: block.bullets };
  return {
    summary: summaryLines.join("\n").replace(/\n{3,}/g, "\n\n").trim(),
    bullets,
  };
}

export function renderOperatingContext(
  doc: jsPDF,
  block: TelecomContextBlock,
  ctx?: Ctx,
): void {
  doc.addPage();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const bottomLimit = pageH - 60;

  drawRunningHeader(doc, {
    clientName: ctx?.clientOrgName || "Client",
    frameworks: ctx?.frameworks ?? null,
    methodology: ctx?.methodology ?? null,
  });

  let y = drawSectionTitle(doc, {
    sectionNumber: "02",
    title: "Operating & Regulatory Context",
    continued: true,
  });

  // Confirmation/source pills are internal to the Reviewer Console and intentionally omitted from the generated report.
  y += 4;

  if (!block.confirmed) {
    const cautionary =
      "Operating and regulatory context was not confirmed by the reviewer at the time of report generation. The context shown is based on available assessment profile and scope inputs and should be reviewed before reliance.";
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9.5);
    doc.setTextColor(125, 75, 10);
    const cLines = doc.splitTextToSize(cautionary, pageW - PAGE_MARGIN * 2);
    doc.text(cLines, PAGE_MARGIN, y, { lineHeightFactor: 1.4 } as any);
    y += cLines.length * 12 + 4;
  }

  const body = normalizeContextBody(block);

  // Summary
  y += 10;
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const summaryText = body.summary.trim();
  if (summaryText) {
    const summaryLines = doc.splitTextToSize(summaryText, pageW - PAGE_MARGIN * 2);
    doc.text(summaryLines, PAGE_MARGIN, y, { lineHeightFactor: 1.5 } as any);
    y += summaryLines.length * 15 + 16;
  }

  // Bullets — proper hanging indent so wrapped lines align under the text,
  // not under the bullet glyph.
  doc.setFontSize(10.5);
  const bulletIndent = 18;            // left edge for bullet marker
  const textIndent = bulletIndent + 12; // wrapped lines align here
  body.bullets.forEach((b) => {
    const lines = doc.splitTextToSize(b, pageW - PAGE_MARGIN * 2 - textIndent);
    // Bullet glyph
    doc.text("•", PAGE_MARGIN + bulletIndent, y, { lineHeightFactor: 1.45 } as any);
    // Hanging-indent body
    doc.text(lines, PAGE_MARGIN + textIndent, y, { lineHeightFactor: 1.45 } as any);
    y += lines.length * 14 + 6;
  });
  y += 8;

  // Frameworks
  if (block.frameworkBlocks.length > 0) {
    y = ensureSpace(doc, y, 60, ctx);
    doc.setTextColor(...REPORT_THEME.accent.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("FRAMEWORKS APPLIED", PAGE_MARGIN, y);
    y += 14;

    for (const fb of block.frameworkBlocks) {
      // Estimate card height; paginate if needed.
      const estimate = estimateFrameworkHeight(doc, fb, pageW);
      if (y + estimate > bottomLimit) {
        drawPageFooter(doc, ctx?.pageNumber ?? 0);
        doc.addPage();
        drawRunningHeader(doc, {
          clientName: ctx?.clientOrgName || "Client",
          frameworks: ctx?.frameworks ?? null,
          methodology: ctx?.methodology ?? null,
        });
        y = drawSectionTitle(doc, {
          sectionNumber: "02",
          title: "Operating & Regulatory Context",
          continued: true,
        });
      }
      y = drawFrameworkCard(doc, fb, PAGE_MARGIN, y, pageW - PAGE_MARGIN * 2);
      y += 8;
    }
  }

  // Notes
  if (block.notes) {
    y = ensureSpace(doc, y, 60, ctx);
    y += 4;
    doc.setTextColor(...REPORT_THEME.accent.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("ADDITIONAL CONTEXT NOTES", PAGE_MARGIN, y);
    y += 14;
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    const notesLines = doc.splitTextToSize(block.notes, pageW - PAGE_MARGIN * 2);
    doc.text(notesLines, PAGE_MARGIN, y, { lineHeightFactor: 1.45 } as any);
    y += notesLines.length * 13 + 8;
  }

  // Legacy methodology (only shown when no frameworks)
  if (block.legacyMethodology) {
    y = ensureSpace(doc, y, 60, ctx);
    y += 4;
    doc.setTextColor(...REPORT_THEME.accent.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("METHODOLOGY NOTES", PAGE_MARGIN, y);
    y += 14;
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    const lines = doc.splitTextToSize(block.legacyMethodology, pageW - PAGE_MARGIN * 2);
    doc.text(lines, PAGE_MARGIN, y, { lineHeightFactor: 1.45 } as any);
    y += lines.length * 13 + 8;
  }

  // Phase 2.14 — Technology & Vendor Exposure
  const vendors = ctx?.vendorExposure ?? [];
  if (vendors.length > 0) {
    const allRows = buildVendorExposureTableRows(vendors);
    const innerW = pageW - PAGE_MARGIN * 2;
    const pageH2 = doc.internal.pageSize.getHeight();
    const bottomLimit2 = pageH2 - 60;

    // Public report shows Vendor, Domain (multi), Scope, Dependency, and a
    // dynamically generated Disruption Impact phrase (<=10 words).
    type Col = { key: keyof typeof allRows[number]; header: string; weight: number };
    const cols: Col[] = [
      { key: "vendor_name",       header: "Vendor",            weight: 1.2 },
      { key: "telecom_domain",    header: "Domain",            weight: 1.3 },
      { key: "deployment_scope",  header: "Scope",             weight: 0.8 },
      { key: "dependency_level",  header: "Dependency",        weight: 0.9 },
      { key: "disruption_impact", header: "Disruption Impact", weight: 2.3 },
    ];

    const totalW = cols.reduce((a, c) => a + c.weight, 0);
    const colWidths = cols.map((c) => (c.weight / totalW) * innerW);

    const CELL_PAD_X = 6;
    const ROW_FONT = 9.5;
    const HEADER_FONT = 9.5;
    const LINE_H = 13;
    const ROW_PAD_Y = 12;

    // Word-aware wrap: never splits inside a token unless the token alone exceeds maxWidth.
    const wrapCell = (text: string, maxWidth: number): string[] => {
      const inner = maxWidth - CELL_PAD_X * 2;
      const words = String(text ?? "").split(/\s+/).filter(Boolean);
      if (words.length === 0) return [""];
      const lines: string[] = [];
      let current = "";
      for (const w of words) {
        const tentative = current ? current + " " + w : w;
        if (doc.getTextWidth(tentative) <= inner) {
          current = tentative;
        } else {
          if (current) lines.push(current);
          if (doc.getTextWidth(w) > inner) {
            // single word too long: fall back to char split for just this word
            const parts = doc.splitTextToSize(w, inner) as string[];
            lines.push(...parts.slice(0, -1));
            current = parts[parts.length - 1] ?? "";
          } else {
            current = w;
          }
        }
      }
      if (current) lines.push(current);
      return lines;
    };

    const measureRow = (cells: string[], bold: boolean): number => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(bold ? HEADER_FONT : ROW_FONT);
      const maxLines = Math.max(
        ...cells.map((c, i) => wrapCell(c, colWidths[i]).length),
      );
      return maxLines * LINE_H + ROW_PAD_Y;
    };

    const drawRow = (cells: string[], bold: boolean, fill: boolean, zebra = false): number => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(bold ? HEADER_FONT : ROW_FONT);
      const wrapped = cells.map((c, i) => wrapCell(c, colWidths[i]));
      const rowH = Math.max(...wrapped.map((l) => l.length)) * LINE_H + ROW_PAD_Y;
      if (bold) {
        // Lighter accent-blue header with gold left accent + white text
        doc.setFillColor(...REPORT_THEME.accent.rgb);
        doc.rect(PAGE_MARGIN, y - 2, innerW, rowH, "F");
        doc.setFillColor(...REPORT_THEME.gold.rgb);
        doc.rect(PAGE_MARGIN, y - 2, 3, rowH, "F");
        doc.setTextColor(255, 255, 255);
      } else if (zebra) {
        // Inset zebra fill by 1pt top & bottom so neighbouring row dividers
        // are never painted over by this row's background — root cause of
        // the intermittent missing horizontal borders in the Vendor table.
        doc.setFillColor(247, 249, 252);
        doc.rect(PAGE_MARGIN, y - 1, innerW, rowH - 2, "F");
        doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
      } else {
        doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
      }
      // Both top and bottom dividers — guarantees a visible horizontal
      // separator for every rendered row, including the first row after a
      // page break / continuation header.
      doc.setDrawColor(180, 192, 210);
      doc.setLineWidth(0.5);
      doc.line(PAGE_MARGIN, y - 2, PAGE_MARGIN + innerW, y - 2);
      doc.line(PAGE_MARGIN, y + rowH - 2, PAGE_MARGIN + innerW, y + rowH - 2);
      let cx = PAGE_MARGIN;
      wrapped.forEach((lines, i) => {
        const key = cols[i]?.key;
        const raw = cells[i] ?? "";
        // High-risk badge
        if (!bold && key === "high_risk_flag" && /yes/i.test(raw)) {
          const badgeW = doc.getTextWidth("YES") + 10;
          doc.setFillColor(220, 53, 69);
          doc.roundedRect(cx + CELL_PAD_X, y + 2, badgeW, LINE_H + 1, 2, 2, "F");
          doc.setTextColor(255, 255, 255);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(ROW_FONT);
          doc.text("YES", cx + CELL_PAD_X + 5, y + LINE_H);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
        } else if (!bold && key === "vendor_name") {
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...REPORT_THEME.navy.rgb);
          doc.text(lines, cx + CELL_PAD_X, y + LINE_H, { lineHeightFactor: 1.35 } as any);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
        } else {
          doc.text(lines, cx + CELL_PAD_X, y + LINE_H, { lineHeightFactor: 1.35 } as any);
        }
        cx += colWidths[i];
      });
      return rowH;
    };

    const drawSectionHeading = (continued: boolean) => {
      // Accent bar on the left for strong visual anchor
      doc.setFillColor(...REPORT_THEME.accent.rgb);
      doc.rect(PAGE_MARGIN, y - 11, 4, 18, "F");

      doc.setTextColor(...REPORT_THEME.navy.rgb);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      const title = continued
        ? "Technology & Vendor Exposure (continued)"
        : "Technology & Vendor Exposure";
      doc.text(title, PAGE_MARGIN + 10, y + 2);

      // Confirmation pill is internal to the Reviewer Console and intentionally omitted from the generated report.
      y += 20;

      if (!continued) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(110, 110, 120);
        const discLines = doc.splitTextToSize(VENDOR_EXPOSURE_DISCLAIMER, innerW);
        doc.text(discLines, PAGE_MARGIN, y, { lineHeightFactor: 1.4 } as any);
        y += discLines.length * 11 + 10;
      } else {
        y += 6;
      }
    };

    const newPageForTable = (continued: boolean) => {
      drawPageFooter(doc, ctx?.pageNumber ?? 0);
      doc.addPage();
      drawRunningHeader(doc, {
        clientName: ctx?.clientOrgName || "Client",
        frameworks: ctx?.frameworks ?? null,
        methodology: ctx?.methodology ?? null,
      });
      y = drawSectionTitle(doc, {
        sectionNumber: "02",
        title: "Operating & Regulatory Context",
        continued: true,
      });
      y += 22;
      drawSectionHeading(continued);
    };

    // === Always render Technology & Vendor Exposure on its own page ===
    const headerCells = cols.map((c) => c.header);
    newPageForTable(false);

    // Header row
    y += drawRow(headerCells, true, true);

    // Data rows
    let rowIdx = 0;
    for (const r of allRows) {
      const rowCells = cols.map((c) => String(r[c.key] ?? ""));
      const rowH = measureRow(rowCells, false);

      if (y + rowH > bottomLimit2) {
        newPageForTable(true);
        y += drawRow(headerCells, true, true);
        rowIdx = 0;
      }
      y += drawRow(rowCells, false, false, rowIdx % 2 === 1);
      rowIdx++;

      // Reviewer notes are intentionally omitted from the public report;
      // they appear only in the internal executive-level report.
    }
    y += 4;
  }

  drawPageFooter(doc, ctx?.pageNumber ?? 0);
}

function ensureSpace(doc: jsPDF, y: number, needed: number, ctx?: Ctx): number {
  const pageH = doc.internal.pageSize.getHeight();
  if (y + needed > pageH - 60) {
    drawPageFooter(doc, ctx?.pageNumber ?? 0);
    doc.addPage();
    drawRunningHeader(doc, {
      clientName: ctx?.clientOrgName || "Client",
      frameworks: ctx?.frameworks ?? null,
      methodology: ctx?.methodology ?? null,
    });
    return drawSectionTitle(doc, {
      sectionNumber: "02",
      title: "Operating & Regulatory Context",
      continued: true,
    });
  }
  return y;
}

function estimateFrameworkHeight(
  doc: jsPDF,
  fb: { framework: string; applicability: string; requirements: string; purpose: string },
  pageW: number,
): number {
  doc.setFontSize(10);
  const w = pageW - PAGE_MARGIN * 2 - 24;
  const a = doc.splitTextToSize("Applicability: " + fb.applicability, w).length;
  const r = doc.splitTextToSize("Requirements: " + fb.requirements, w).length;
  const p = doc.splitTextToSize("Purpose: " + fb.purpose, w).length;
  return 24 + (a + r + p) * 13 + 18;
}

function drawFrameworkCard(
  doc: jsPDF,
  fb: { framework: string; applicability: string; requirements: string; purpose: string },
  x: number,
  y: number,
  w: number,
): number {
  const startY = y;
  doc.setFillColor(...REPORT_THEME.surfaceAlt.rgb);
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.6);

  // Title
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  const titleY = y + 18;
  let contentY = titleY + 6;

  // Body
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const innerW = w - 24;

  const sections: Array<[string, string]> = [
    ["Applicability", fb.applicability],
    ["Requirements", fb.requirements],
    ["Purpose", fb.purpose],
  ];

  const lineHeights: number[] = [];
  const splitSections = sections.map(([label, value]) => {
    const lines = doc.splitTextToSize(`${label}: ${value}`, innerW);
    lineHeights.push(lines.length);
    return lines;
  });
  const totalLines = lineHeights.reduce((a, b) => a + b, 0);
  const cardH = 24 + totalLines * 13 + 12;

  // Background + border
  doc.rect(x, startY, w, cardH, "FD");

  // Re-render title on top of fill
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  doc.text(fb.framework, x + 12, titleY);

  // Render sections
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  let cy = contentY + 6;
  splitSections.forEach((lines) => {
    doc.text(lines, x + 12, cy, { lineHeightFactor: 1.4 } as any);
    cy += lines.length * 13 + 2;
  });

  return startY + cardH;
}
